/**
 * Community's pure half — `docs/plans/community.md`.
 *
 * What a post's text means (cashtags), how its comments nest, how an app object
 * becomes an attachment and an attachment becomes a layout again. Pure and
 * tested here, for `layout-templates.ts`'s reason: the component is the one
 * place this app cannot test.
 */

import type { ChartGroup, Layout } from "@qufolio/model";

import type { CommunityRole, Embed, Post, Topic } from "../api/community";
import { intervalLabel } from "./interval-label";

// ── text ─────────────────────────────────────────────────────────────────────

export type Segment =
  | { kind: "text"; text: string }
  | { kind: "cashtag"; symbol: string }
  | { kind: "link"; href: string };

/**
 * `$NVDA`, `$BRK.B`, `$RELIANCE.NS`, `$AAPL/SPY` — a dollar, then a capital.
 *
 * A capital first is what keeps `$5` and `$5.20` prose: a price is never a
 * symbol. Lower case is not a cashtag either, so `$var` in a pasted formula
 * stays text.
 */
const CASHTAG = /\$([A-Z][A-Z0-9]{0,9}(?:[.-][A-Z0-9]{1,4})?(?:\/[A-Z][A-Z0-9]{0,9})?)(?![A-Za-z0-9])/g;
const LINK = /\bhttps?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]/g;

/** A post's text as plain segments — never HTML, so nothing a member types can run. */
export function tokenize(text: string): Segment[] {
  const marks: { start: number; end: number; seg: Segment }[] = [];
  for (const m of text.matchAll(CASHTAG)) {
    marks.push({ start: m.index!, end: m.index! + m[0].length, seg: { kind: "cashtag", symbol: m[1]! } });
  }
  for (const m of text.matchAll(LINK)) {
    const start = m.index!;
    const end = start + m[0].length;
    if (marks.some((x) => x.start < end && start < x.end)) continue;
    marks.push({ start, end, seg: { kind: "link", href: m[0] } });
  }
  marks.sort((a, b) => a.start - b.start);
  const out: Segment[] = [];
  let at = 0;
  for (const mark of marks) {
    if (mark.start < at) continue;
    if (mark.start > at) out.push({ kind: "text", text: text.slice(at, mark.start) });
    out.push(mark.seg);
    at = mark.end;
  }
  if (at < text.length) out.push({ kind: "text", text: text.slice(at) });
  return out;
}

/** Every distinct symbol a post names, in order. */
export function cashtags(text: string): string[] {
  const seen: string[] = [];
  for (const seg of tokenize(text)) {
    if (seg.kind === "cashtag" && !seen.includes(seg.symbol)) seen.push(seg.symbol);
  }
  return seen;
}

/** `4m`, `2h`, `3d`, then the date. Short, because a list row is narrow. */
export function relTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const then = new Date(iso);
  const seconds = Math.max(0, (now.getTime() - then.getTime()) / 1000);
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)}d`;
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// ── threads ──────────────────────────────────────────────────────────────────

export interface CommentNode {
  post: Post;
  children: CommentNode[];
}

/**
 * Comments as a tree, oldest first at each level.
 *
 * A comment whose parent is not in the list — a reply to something the reader
 * cannot see, such as another visitor's pending comment — is shown at the top
 * level rather than dropped: losing a reply is worse than misplacing it.
 */
export function nestComments(threadId: string, comments: readonly Post[]): CommentNode[] {
  const nodes = new Map<string, CommentNode>();
  for (const post of comments) nodes.set(post.id, { post, children: [] });
  const roots: CommentNode[] = [];
  for (const post of comments) {
    const node = nodes.get(post.id)!;
    const parent = post.parentId && post.parentId !== threadId ? nodes.get(post.parentId) : undefined;
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}

export function runsSpace(role: CommunityRole | undefined): boolean {
  return role === "host" || role === "admin";
}

/** Whether this role may start a thread in this topic. Mirrors the server. */
export function canStartThread(role: CommunityRole | undefined, topic: Topic | undefined): boolean {
  if (!role || !topic || topic.archived) return false;
  if (role === "outsider") return false;
  if (role === "visitor") return topic.isOpen;
  return topic.postPolicy === "members" || runsSpace(role);
}

// ── attachments ──────────────────────────────────────────────────────────────

const BASE_ALIASES = new Set(["open", "high", "low", "close", "volume"]);

/** The indicator aliases a chart draws, without the price and volume themselves. */
export function chartIndicators(group: ChartGroup): string[] {
  const out: string[] = [];
  for (const pane of group.panes ?? []) {
    for (const plot of pane.plots ?? []) {
      const alias = plot.outputAlias;
      if (alias && !BASE_ALIASES.has(alias) && !out.includes(alias)) out.push(alias);
    }
  }
  return out;
}

/** A chart as an attachment: its picture (if one was taken) and a copy of itself. */
export function chartEmbed(group: ChartGroup, snapshotId?: string, takenAt = new Date().toISOString()): Embed {
  return {
    kind: "chart",
    ...(snapshotId ? { snapshotId } : {}),
    symbol: group.symbol,
    interval: intervalLabel(group.frequency),
    indicators: chartIndicators(group),
    group: JSON.parse(JSON.stringify(group)) as Record<string, unknown>,
    takenAt,
  };
}

/** A layout as an attachment: the arrangement and every chart in it. */
export function layoutEmbed(layout: Layout, snapshotIds: string[] = []): Embed {
  const symbols = layout.sections
    .filter((s) => s.kind === "chartGroup")
    .map((s) => (s.config as ChartGroup | undefined)?.symbol)
    .filter((s): s is string => !!s);
  return {
    kind: "layout",
    name: layout.name,
    layout: JSON.parse(JSON.stringify({ ...layout, hidden: undefined, source: undefined })) as Record<string, unknown>,
    symbols,
    ...(snapshotIds.length ? { snapshotIds } : {}),
  };
}

/**
 * A shared chart as a one-chart layout definition, for `adoptLayout`.
 *
 * **Open live adds a layout rather than replacing a chart.** Pointing the
 * reader's active chart at someone else's setup would discard theirs; a new tab
 * costs nothing and is closed with one click.
 */
export function layoutFromChartEmbed(embed: Extract<Embed, { kind: "chart" }>): Partial<Layout> | null {
  if (!embed.group) return null;
  return {
    rows: 1,
    columns: 1,
    sections: [
      {
        id: "chart",
        kind: "chartGroup",
        row: 0,
        column: 0,
        config: embed.group as unknown as ChartGroup,
      },
    ],
  } as Partial<Layout>;
}

/** The name a copied chart's layout tab gets. */
export function sharedChartName(embed: Extract<Embed, { kind: "chart" }>, author: string): string {
  return `${embed.symbol}${embed.interval ? ` · ${embed.interval}` : ""} (${author})`.slice(0, 48);
}

/**
 * The indicator categories a chart image would show to people outside the group.
 *
 * Standard indicators are everyone's; anything else is a category somebody
 * pays for, and the composer warns before it goes into the Open House.
 */
export function gatedCategories(
  aliases: readonly string[],
  categoryOf: (alias: string) => string | undefined,
): string[] {
  const out: string[] = [];
  for (const alias of aliases) {
    const category = categoryOf(alias);
    if (category && category.toLowerCase() !== "stnd" && category.toLowerCase() !== "standard" && !out.includes(category)) {
      out.push(category);
    }
  }
  return out;
}

/** What an attachment chip reads, before it is posted. */
export function embedLabel(embed: Embed): string {
  switch (embed.kind) {
    case "chart":
      return `${embed.symbol}${embed.interval ? ` · ${embed.interval}` : ""}${
        embed.indicators?.length ? ` · ${embed.indicators.length} indicator${embed.indicators.length > 1 ? "s" : ""}` : ""
      }`;
    case "layout":
      return `Layout: ${embed.name} (${embed.symbols?.length ?? 0} charts)`;
    case "screen":
      return `Screen: ${embed.name}`;
    case "plan":
      return `Plan: ${embed.symbol} ${embed.direction} ${embed.entry} / ${embed.stop}`;
    case "tracklist":
      return `List: ${embed.name} (${embed.symbols.length})`;
  }
}

/** Reward over risk for a plan card, to two places; null when it has no target. */
export function planR(embed: Extract<Embed, { kind: "plan" }>): number | null {
  const risk = Math.abs(embed.entry - embed.stop);
  const target = embed.targets[0];
  if (!risk || target === undefined) return null;
  return Math.round((Math.abs(target - embed.entry) / risk) * 100) / 100;
}
